import { useMemo, useState } from 'react';

import { getViewport } from '../../../util/responsive/media-queries';
import { getViewportLowerBoundary, type Viewport } from '../../../util/responsive/types';
import { InitialWindowSize } from '../constants';

export interface UseViewportStateParams {
  /**
   * Seeds the initial window size (in pixels) the provider's state is initialized with, before any
   * client-side resize listener has measured the real window.
   *
   * This is used to seed the state from a server-derived {@link ViewportSeed} so that the initial
   * SSR render and the client's first render agree.  When omitted, the initial size falls back to
   * {@link InitialWindowSize}, or to the lower boundary of {@link UseViewportStateParams.viewport}
   * when a constant viewport is held.
   */
  readonly initialSize?: number;
  /**
   * Seeds the initial {@link Viewport} the provider reports, rather than deriving it from the
   * window size for the first render.
   *
   * This is used to seed the state from a server-derived {@link ViewportSeed} so that the initial
   * SSR render and the client's first render agree, and by tests to hold the viewport constant
   * where there is no 'window' to measure.  After mount, the resize listener replaces it with the
   * {@link Viewport} derived from the measured window size.
   */
  readonly viewport?: Viewport;
}

export const useViewportState = (params: UseViewportStateParams) => {
  const [size, setSize] = useState<number>(
    () =>
      params.initialSize ??
      (params.viewport === undefined
        ? InitialWindowSize
        : getViewportLowerBoundary(params.viewport)),
  );

  const [viewport, setViewport] = useState<Viewport>(
    () => params.viewport ?? getViewport({ size }),
  );

  return useMemo(() => ({ setSize, setViewport, size, viewport }), [viewport, size]);
};
